import { useState, useCallback, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import DropZone from "../../components/DropZone";
import PDFAnalyzer from "../../components/PDFAnalyzer";
import ToolFAQ from "../../components/ToolFAQ";
import RelatedTools from "../../components/RelatedTools";
import SharePanel from "../../components/SharePanel";
import { AdZone } from "../../components/AdBanner";
import JsonLD, { buildBreadcrumb, buildFAQSchema } from "../../components/JsonLD";
import { Progress } from "../../components/ui/progress";
import { useLanguage } from "../../context/LanguageContext";
import { SEO_CONTENT, SEO_CONTENT_I18N, LONG_TAIL_SEO } from "../../seoContent";
import { SITE_NAME, SITE_URL } from "../../translations";
import { SEO_PAGES_CONFIG } from "../../seoPagesConfig";
import { getSlugFromPath } from "../../i18nConfig";
import { useHreflang } from "../../hooks/useHreflang";
import { trackEvent } from "../../utils/analytics";
import { ChevronLeft, Download, RotateCcw, CheckCircle, AlertCircle, Loader2, TrendingDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RELATED_GUIDES = {
  merge: [
    { slug: "how-to-merge-pdf-files-online", label: "How to merge PDF files online for free" },
  ],
  "merge-pdf-online-free": [
    { slug: "how-to-merge-pdf-files-online", label: "How to combine PDF files into one document" },
  ],
  compress: [
    { slug: "how-to-reduce-pdf-file-size-without-losing-quality", label: "How to reduce PDF file size without losing quality" },
    { slug: "how-to-compress-pdf-for-email", label: "How to compress a PDF for email" },
  ],
  "compress-pdf-without-losing-quality": [
    { slug: "how-to-reduce-pdf-file-size-without-losing-quality", label: "Guide to reducing PDF size without visible quality loss" },
    { slug: "how-to-compress-pdf-for-email", label: "Compress PDFs for Gmail and Outlook attachments" },
  ],
  excelToCsv: [
    { slug: "how-to-convert-excel-to-csv", label: "How to convert Excel to CSV online" },
    { slug: "csv-vs-excel-difference", label: "CSV vs XLSX: key differences" },
  ],
  "convert-excel-to-csv": [
    { slug: "how-to-convert-excel-to-csv", label: "Step-by-step Excel to CSV guide" },
    { slug: "csv-vs-excel-difference", label: "When to use CSV instead of XLSX" },
  ],
  csvToExcel: [
    { slug: "csv-vs-excel-difference", label: "CSV vs XLSX: when to convert formats" },
  ],
};

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(2)} MB`;
}

export default function ToolPage({ config }) {
  const { t, lang } = useLanguage();
  const location = useLocation();
  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [extraValues, setExtraValues] = useState({});
  const [analyzeInfo, setAnalyzeInfo] = useState(null);
  const [compressStats, setCompressStats] = useState(null);
  const autoDownloadRef = useRef(false);

  const toolKey = config.toolKey;
  // Strip lang prefix to get the clean slug (e.g. /fr/merge-pdf → merge-pdf)
  const cleanSlug = getSlugFromPath(location.pathname);
  const longTailSlug = cleanSlug;
  const longTail = LONG_TAIL_SEO[longTailSlug]?.[lang] || LONG_TAIL_SEO[longTailSlug]?.en;
  const baseSeo = toolKey
    ? SEO_CONTENT[toolKey]?.[lang] || SEO_CONTENT_I18N?.[toolKey]?.[lang] || SEO_CONTENT[toolKey]?.en
    : null;
  const seo = longTail || baseSeo;

  // Inject hreflang for primary tool routes only (not long-tail slugs)
  const isPrimarySlug = !!(toolKey && SEO_PAGES_CONFIG.find((p) => p.slug === cleanSlug));
  useHreflang(isPrimarySlug ? cleanSlug : null, "tool");
  const faqs = seo?.faqs || [];

  // Resolve category for 3-level BreadcrumbList
  const pageConfig = SEO_PAGES_CONFIG.find(
    (p) => p.toolKey === toolKey || p.longTailSlugs?.includes(longTailSlug)
  );
  const breadcrumbCategory = pageConfig?.categorySlug
    ? { name: pageConfig.categoryName, slug: pageConfig.categorySlug }
    : null;

  // Update document title, meta description and canonical link for long-tail/primary pages
  useEffect(() => {
    if (longTail?.title) {
      document.title = longTail.title;
    } else if (baseSeo?.title) {
      document.title = baseSeo.title;
    } else {
      document.title = `${config.title} — ${SITE_NAME}`;
    }
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute("content", longTail?.description || baseSeo?.description || config.description);
    }
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = `${SITE_URL}${location.pathname}`;
  }, [longTail, baseSeo, config.title, config.description, location.pathname]);

  const canProcess = config.canProcessOverride
    ? config.canProcessOverride(files, extraValues)
    : files.length > 0 && (!config.multiple || files.length >= 2);

  // Analytics: upload_file event
  useEffect(() => {
    if (files.length > 0) {
      trackEvent("upload_file", { tool: toolKey, count: files.length });
    }
  }, [files.length, toolKey]);

  // Auto-analyze PDF
  useEffect(() => {
    if (files.length === 0 || !files[0].name.toLowerCase().endsWith(".pdf")) {
      setAnalyzeInfo(null);
      return;
    }
    const analyzeFile = async () => {
      const formData = new FormData();
      formData.append("file", files[0]);
      try {
        const res = await axios.post(`${API}/pdf/analyze`, formData);
        setAnalyzeInfo(res.data);
      } catch (_) { setAnalyzeInfo(null); }
    };
    analyzeFile();
  }, [files]);

  // Auto-download for binary results
  useEffect(() => {
    if (result?.type === "download" && !autoDownloadRef.current) {
      autoDownloadRef.current = true;
      const timer = setTimeout(() => {
        const a = document.createElement("a");
        a.href = result.url;
        a.download = result.filename;
        a.click();
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [result]);

  const handleProcess = useCallback(async () => {
    setProcessing(true);
    setProgress(0);
    setError(null);
    setResult(null);
    setCompressStats(null);
    autoDownloadRef.current = false;
    trackEvent("tool_used", { tool: toolKey });

    const formData = new FormData();
    files.forEach((f) => formData.append(config.multiple ? "files" : "file", f));
    Object.entries(extraValues).forEach(([k, v]) => formData.append(k, v));

    try {
      const response = await axios.post(`${API}${config.endpoint}`, formData, {
        responseType: config.resultType === "text" ? "json" : "arraybuffer",
        onUploadProgress: (e) => {
          const pct = e.total ? Math.round((e.loaded / e.total) * 70) : 30;
          setProgress(pct);
        },
      });
      setProgress(95);

      if (config.showCompressionStats) {
        const origSize = parseInt(response.headers["x-original-size"] || "0");
        const compSize = parseInt(response.headers["x-compressed-size"] || "0");
        if (origSize > 0 && compSize > 0) setCompressStats({ original: origSize, compressed: compSize });
      }

      axios.post(`${API}/stats/increment`).catch(() => {});
      trackEvent("conversion_complete", { tool: toolKey, resultType: config.resultType });

      if (config.resultType === "text") {
        setResult({ type: "text", data: response.data });
      } else if (config.resultType === "image") {
        const blob = new Blob([response.data], { type: "image/jpeg" });
        setResult({ type: "image", url: URL.createObjectURL(blob) });
      } else {
        const mimeMap = { ".pdf": "application/pdf", ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document", ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", ".zip": "application/zip" };
        const ext = config.resultFileName.slice(config.resultFileName.lastIndexOf("."));
        const blob = new Blob([response.data], { type: mimeMap[ext] || "application/octet-stream" });
        setResult({ type: "download", url: URL.createObjectURL(blob), filename: config.resultFileName });
      }
      setProgress(100);
    } catch (err) {
      let msg = t.upload.errorTitle;
      if (err.response?.data) {
        try { const json = JSON.parse(new TextDecoder().decode(err.response.data)); msg = json.detail || msg; } catch (_) {}
      }
      setError(msg);
    } finally {
      setProcessing(false);
    }
  }, [files, config, extraValues, t, toolKey]);

  const handleReset = () => {
    setFiles([]); setResult(null); setError(null); setProgress(0);
    setExtraValues({}); setAnalyzeInfo(null); setCompressStats(null);
    autoDownloadRef.current = false;
  };

  const compressionPercent = compressStats && compressStats.original > compressStats.compressed
    ? Math.round((1 - compressStats.compressed / compressStats.original) * 100) : null;

  const h1Title = seo?.h1 || config.title;
  const relatedGuides = lang === "en"
    ? (RELATED_GUIDES[longTailSlug] || RELATED_GUIDES[toolKey] || [])
    : [];

  return (
    <div className="min-h-screen bg-white page-fade-in">
      <JsonLD data={buildBreadcrumb(config.title, location.pathname, breadcrumbCategory)} />
      {faqs.length > 0 && <JsonLD data={buildFAQSchema(faqs)} />}
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-600 transition-colors" data-testid="breadcrumb-home">
            <ChevronLeft className="w-4 h-4" />{t.nav.home}
          </Link>
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${config.color}15` }}>
            <config.Icon className="w-7 h-7" style={{ color: config.color }} />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="tool-page-title">
            {h1Title}
          </h1>
          <p className="text-slate-500 text-base">{config.description}</p>
        </div>

        {/* Ad Zone — Top */}
        <AdZone className="mb-6" />

        {/* Main Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
          {!result && (
            <>
              <DropZone accept={config.accept} multiple={config.multiple} files={files} onChange={setFiles} hint={config.hint} hintSub={config.hintSub} />
              {analyzeInfo && <PDFAnalyzer analysis={analyzeInfo} />}
              {config.extraFields && files.length > 0 && (
                <div className="mt-5">{config.extraFields({ values: extraValues, onChange: setExtraValues, t })}</div>
              )}
              {files.length > 0 && (
                <div className="mt-6">
                  <button
                    onClick={handleProcess}
                    disabled={processing || !canProcess}
                    data-testid="process-btn"
                    className="w-full py-3.5 rounded-xl bg-blue-600 text-white font-semibold text-base hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.99] transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center gap-2"
                  >
                    {processing ? <><Loader2 className="w-5 h-5 spinner" />{t.upload.processing}</> : config.action}
                  </button>
                </div>
              )}
              {processing && (
                <div className="mt-4">
                  <Progress value={progress} className="h-2" data-testid="progress-bar" />
                  <p className="text-xs text-slate-500 mt-2 text-center">{progress}%</p>
                </div>
              )}
              {error && (
                <div className="mt-4 flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200" data-testid="error-message">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-700 text-sm">{t.upload.errorTitle}</p>
                    <p className="text-red-600 text-sm mt-0.5">{error}</p>
                  </div>
                </div>
              )}
            </>
          )}

          {result && (
            <div className="text-center py-4 page-fade-in" data-testid="result-section">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <p className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{t.upload.success}</p>
              <p className="text-slate-500 text-sm mb-4">{t.upload.downloadReady}</p>

              {compressStats && (
                <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-200 text-left" data-testid="compress-stats">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingDown className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-800 text-sm">{lang === "fr" ? "Résultat de la compression" : "Compression Result"}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div><p className="text-xs text-slate-500 mb-1">{lang === "fr" ? "Taille originale" : "Original size"}</p><p className="font-semibold text-slate-800 text-sm">{formatBytes(compressStats.original)}</p></div>
                    <div><p className="text-xs text-slate-500 mb-1">{lang === "fr" ? "Taille compressée" : "Compressed size"}</p><p className="font-semibold text-green-700 text-sm">{formatBytes(compressStats.compressed)}</p></div>
                    <div><p className="text-xs text-slate-500 mb-1">{lang === "fr" ? "Réduction" : "Reduction"}</p><p className="font-bold text-green-700 text-base">{compressionPercent ? `-${compressionPercent}%` : "~0%"}</p></div>
                  </div>
                </div>
              )}

              {result.type === "download" && (
                <div className="space-y-1">
                  <button onClick={() => { const a = document.createElement("a"); a.href = result.url; a.download = result.filename; a.click(); }} data-testid="download-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 active:scale-95 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                    <Download className="w-5 h-5" />{t.upload.download}
                  </button>
                  <p className="text-xs text-slate-500">{lang === "fr" ? "Téléchargement automatique en cours..." : "Auto-downloading..."}</p>
                </div>
              )}

              {result.type === "image" && (
                <div className="space-y-4">
                  <img src={result.url} alt="PDF Preview" className="max-w-full rounded-xl border border-slate-200 shadow-md mx-auto" data-testid="preview-image" />
                  <a href={result.url} download="preview.jpg" data-testid="download-image-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 active:scale-95 transition-all">
                    <Download className="w-5 h-5" />{config.downloadImageLabel || t.upload.download}
                  </a>
                </div>
              )}

              {result.type === "text" && (
                <div className="text-left space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">{result.data.page_count} {config.pagesLabel}</span>
                    <div className="flex gap-2">
                      <button onClick={() => navigator.clipboard.writeText(result.data.text)} data-testid="copy-text-btn" className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">{config.copyLabel}</button>
                      <a href={`data:text/plain;charset=utf-8,${encodeURIComponent(result.data.text)}`} download="extracted_text.txt" data-testid="download-txt-btn" className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors">{config.downloadTxtLabel}</a>
                    </div>
                  </div>
                  <textarea readOnly value={result.data.text} data-testid="extracted-text-area" className="w-full h-72 p-4 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-700 font-mono resize-none focus:outline-none" />
                </div>
              )}

              {/* Share Panel */}
              <SharePanel toolName={config.title} compressReduction={compressionPercent} />

              <button onClick={handleReset} data-testid="try-again-btn" className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors">
                <RotateCcw className="w-4 h-4" />{t.upload.tryAgain}
              </button>
            </div>
          )}
        </div>

        {/* Ad Zone — Middle */}
        <AdZone className="my-8" />

        {/* SEO Text */}
        {seo?.seoText && (
          <div className="mt-2 p-6 bg-slate-50 rounded-2xl border border-slate-100">
            <p className="text-slate-600 text-sm leading-relaxed">{seo.seoText}</p>
          </div>
        )}

        {/* Related Tools */}
        {toolKey && <RelatedTools toolKey={toolKey} />}

        {/* Related Guides */}
        {relatedGuides.length > 0 && (
          <section className="mt-8" data-testid="related-guides">
            <h2
              className="text-lg font-bold text-slate-800 mb-4"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Related Guides
            </h2>
            <div className="space-y-3">
              {relatedGuides.map((guide) => (
                <Link
                  key={guide.slug}
                  to={`/blog/${guide.slug}`}
                  className="block p-4 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm transition-all"
                >
                  <span className="text-sm font-medium text-slate-700 hover:text-blue-600">
                    {guide.label}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* FAQ */}
        {faqs.length > 0 && <ToolFAQ faqs={faqs} />}
      </main>
      <Footer />
    </div>
  );
}
